import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MetricInfo } from "./MetricInfo";
import { 
  BarChart3, 
  RefreshCw, 
  Loader2, 
  Users, 
  PoundSterling, 
  Target, 
  ArrowUpRight, 
  ArrowDownRight,
  TrendingUp,
  Eye,
  FileText,
  Crown,
  AlertCircle,
  Hammer,
  Star,
  ChevronRight,
  ExternalLink,
  Calendar,
  CheckCircle,
  Clock,
  MessageSquare,
  Image,
  X,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { JOB_STATUSES, JOB_TAGS, getStatusInfo, formatDate } from "@/lib/constants";

const API = `${window.location.origin}/api`;

export default function AnalyticsTab({ authHeaders, onNavigateToJob }) {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [enhancedAnalytics, setEnhancedAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsView, setAnalyticsView] = useState('overview');
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [portfolioStats, setPortfolioStats] = useState(null);
  const [pipelineStats, setPipelineStats] = useState(null);
  
  // Drill-down state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownData, setDrillDownData] = useState([]);
  const [drillDownType, setDrillDownType] = useState('');
  
  // Full data for drill-down
  const [allEnquiries, setAllEnquiries] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [allFeedback, setAllFeedback] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  
  // New analytics data
  const [revenueByService, setRevenueByService] = useState([]);
  const [averageJobValue, setAverageJobValue] = useState(0);
  const [customerLTV, setCustomerLTV] = useState(null);
  const [tagAnalytics, setTagAnalytics] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const [basicResponse, enhancedResponse, feedbackRes, portfolioRes, enquiriesRes, invoicesRes, quotesRes, customersRes, revenueServiceRes, avgJobRes, customerLtvRes, tagAnalyticsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, authHeaders),
        axios.get(`${API}/admin/analytics/enhanced`, authHeaders),
        axios.get(`${API}/admin/feedback`, authHeaders).catch(() => ({ data: { feedback: [], stats: {} } })),
        axios.get(`${API}/admin/portfolio`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/enquiries?include_hidden=true`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/quotes`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/customers`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/analytics/revenue-by-service`, authHeaders).catch(() => ({ data: { revenue_by_service: [] } })),
        axios.get(`${API}/admin/analytics/average-job-value`, authHeaders).catch(() => ({ data: { average_job_value: 0 } })),
        axios.get(`${API}/admin/analytics/customer-lifetime-value`, authHeaders).catch(() => ({ data: { customers: [], average_lifetime_value: 0 } })),
        axios.get(`${API}/admin/analytics/tags`, authHeaders).catch(() => ({ data: { tag_counts: {}, total_jobs: 0, jobs_with_tags: 0, tag_adoption_rate: 0 } }))
      ]);
      setAnalyticsData(basicResponse.data);
      setEnhancedAnalytics(enhancedResponse.data);
      
      // New analytics data
      setRevenueByService(revenueServiceRes.data.revenue_by_service || []);
      setAverageJobValue(avgJobRes.data.average_job_value || 0);
      setCustomerLTV(customerLtvRes.data);
      setTagAnalytics(tagAnalyticsRes.data);
      
      // Store full data for drill-down
      const enquiries = enquiriesRes.data || [];
      const invoices = invoicesRes.data || [];
      const quotes = quotesRes.data || [];
      const customers = customersRes.data || [];
      const feedback = feedbackRes.data.feedback || feedbackRes.data || [];
      
      setAllEnquiries(enquiries);
      setAllInvoices(invoices);
      setAllQuotes(quotes);
      setAllCustomers(customers);
      setAllFeedback(feedback);
      
      // Process feedback stats
      const avgRating = feedback.length > 0 
        ? (feedback.reduce((sum, f) => sum + (f.overall_rating || f.rating || 0), 0) / feedback.length).toFixed(1) 
        : 0;
      setFeedbackStats({
        total: feedback.length,
        avgRating,
        approved: feedback.filter(f => f.approved).length,
        pending: feedback.filter(f => !f.approved).length,
        featured: feedback.filter(f => f.featured).length
      });
      
      // Process portfolio stats
      const portfolio = portfolioRes.data || [];
      setPortfolioStats({
        total: portfolio.length,
        featured: portfolio.filter(p => p.featured).length,
        byCategory: portfolio.reduce((acc, p) => {
          const cat = p.category || 'other';
          acc[cat] = (acc[cat] || 0) + 1;
          return acc;
        }, {})
      });
      
      // Iter 65/66: BRIDGE THE SALES FUNNEL.
      // Historical paid/invoiced work often lives ONLY as an invoice with no
      // matching enquiry — which makes the funnel show "100% lost" at
      // Quote→Accepted because the denominator is wrong.
      // Fix: synthesize a virtual enquiry for every invoice whose (job_id OR
      // customer_name OR customer_phone) can't be matched back to a real
      // enquiry. The virtual stage is the invoice's own status
      // (paid → 'paid', sent → 'invoiced').  Iter 66 adds phone-based
      // matching so that a renamed customer (e.g. "J. Smith" vs "John
      // Smith") still links to the same enquiry record.
      const normPhone = (p) => (String(p || '').replace(/\D/g, '').slice(-10));
      const enquiryJobIdSet = new Set(enquiries.map(e => e.id));
      const enquiryNameSet = new Set(
        enquiries.map(e => (e.name || '').trim().toLowerCase()).filter(Boolean)
      );
      const enquiryPhoneSet = new Set(
        enquiries.map(e => normPhone(e.phone)).filter(p => p && p.length >= 7)
      );
      const virtualEnquiries = [];
      for (const inv of invoices) {
        const invPhone = normPhone(inv.customer_phone);
        const linked =
          (inv.job_id && enquiryJobIdSet.has(inv.job_id)) ||
          enquiryNameSet.has((inv.customer_name || '').trim().toLowerCase()) ||
          (invPhone && invPhone.length >= 7 && enquiryPhoneSet.has(invPhone));
        if (linked) continue;
        // Map invoice status → enquiry pipeline stage.
        const mapped = (inv.status === 'paid') ? 'paid'
          : (inv.status === 'sent' || inv.status === 'deposit_paid' || inv.status === 'part_paid') ? 'invoiced'
          : inv.status === 'draft' ? 'completed'
          : 'invoiced';
        virtualEnquiries.push({
          id: `virtual-${inv.id}`,
          name: inv.customer_name || 'Historical Project',
          email: inv.customer_email,
          phone: inv.customer_phone,
          service: 'historical',
          status: mapped,
          source: 'from_invoice',
          _virtual: true,
          created_at: inv.invoice_date || inv.created_at,
          date_completed: inv.invoice_date,
        });
      }
      const pipelineEnquiries = [...enquiries, ...virtualEnquiries];

      // Process pipeline stats with full journey
      const normalizeStatus = (status) => {
        if (!status) return 'new';
        const s = status.toLowerCase().replace(/_/g, '-');
        const mapping = {
          'pending': 'new',
          'inspection-scheduled': 'site-visit',
          'quoted': 'quoted',
          'approved': 'accepted',
          'booked': 'scheduled',
          'in-progress': 'in-progress',
          'completed': 'completed',
          'invoiced': 'invoiced',
          'paid': 'paid',
          'closed': 'paid',
          'rejected': 'rejected',
          'on-hold': 'on-hold'
        };
        return mapping[s] || s;
      };
      
      setPipelineStats({
        // Iter 65: `total` is now "all pipeline entries" (real + bridged from
        // invoices) so the conversion denominators stop producing "100% lost".
        total: pipelineEnquiries.length,
        new: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'new').length,
        siteVisit: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'site-visit').length,
        quoted: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'quoted').length,
        accepted: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'accepted').length,
        scheduled: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'scheduled').length,
        inProgress: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'in-progress').length,
        completed: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'completed').length,
        invoiced: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'invoiced').length,
        paid: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'paid').length,
        reviewRequested: pipelineEnquiries.filter(e => e.review_status === 'requested').length,
        reviewReceived: pipelineEnquiries.filter(e => e.feedback_id).length,
        rejected: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'rejected').length,
        onHold: pipelineEnquiries.filter(e => normalizeStatus(e.status) === 'on-hold').length,
        virtualFromInvoices: virtualEnquiries.length,
      });
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  }, [authHeaders]);
  
  // Drill-down handler
  const openDrillDown = (title, type, filterFn) => {
    let data = [];
    switch (type) {
      case 'enquiries':
        data = filterFn ? allEnquiries.filter(filterFn) : allEnquiries;
        break;
      case 'invoices':
        data = filterFn ? allInvoices.filter(filterFn) : allInvoices;
        break;
      case 'quotes':
        data = filterFn ? allQuotes.filter(filterFn) : allQuotes;
        break;
      case 'customers':
        data = filterFn ? allCustomers.filter(filterFn) : allCustomers;
        break;
      case 'feedback':
        data = filterFn ? allFeedback.filter(filterFn) : allFeedback;
        break;
      default:
        data = [];
    }
    setDrillDownTitle(title);
    setDrillDownType(type);
    setDrillDownData(data);
    setDrillDownOpen(true);
  };
  
  // Render drill-down item based on type
  const renderDrillDownItem = (item, type) => {
    switch (type) {
      case 'enquiries':
        return (
          <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
            <div className="flex-1">
              <p className="font-medium">{item.name}</p>
              <p className="text-sm text-muted-foreground">{item.service?.replace(/-/g, ' ')}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={getStatusInfo(item.status)?.color?.replace('bg-', 'border-')}>
                {item.status || 'new'}
              </Badge>
              {item.estimated_value && (
                <span className="text-sm font-medium text-green-600">£{item.estimated_value}</span>
              )}
            </div>
          </div>
        );
      case 'invoices':
        return (
          <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
            <div className="flex-1">
              <p className="font-medium">{item.reference || item.id?.slice(0, 8)}</p>
              <p className="text-sm text-muted-foreground">{item.customer_name}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={item.status === 'paid' ? 'success' : 'outline'}>
                {item.status || 'draft'}
              </Badge>
              <span className="text-sm font-bold">£{item.total?.toLocaleString() || 0}</span>
            </div>
          </div>
        );
      case 'quotes':
        return (
          <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
            <div className="flex-1">
              <p className="font-medium">{item.reference || item.id?.slice(0, 8)}</p>
              <p className="text-sm text-muted-foreground">{item.customer_name}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{item.status || 'draft'}</Badge>
              <span className="text-sm font-bold">£{item.total?.toLocaleString() || 0}</span>
            </div>
          </div>
        );
      case 'customers':
        return (
          <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
            <div className="flex-1">
              <p className="font-medium">{item.name}</p>
              <p className="text-sm text-muted-foreground">{item.email}</p>
              <p className="text-xs text-muted-foreground">{item.jobs?.length || 0} jobs</p>
            </div>
            <div className="flex items-center gap-2">
              {item.total_spent && (
                <span className="text-sm font-medium text-green-600">£{item.total_spent?.toLocaleString()}</span>
              )}
            </div>
          </div>
        );
      case 'feedback':
        return (
          <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
            <div className="flex-1">
              <p className="font-medium">{item.customer_name}</p>
              <p className="text-sm text-muted-foreground line-clamp-2">{item.comments}</p>
              <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{item.overall_rating || item.rating}</span>
              </div>
              {item.approved && <Badge variant="success">Approved</Badge>}
            </div>
          </div>
        );
      default:
        return <div className="p-3 border rounded-lg">{JSON.stringify(item)}</div>;
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return (
    <div className="space-y-6">
      {/* Header with View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">Business Analytics</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden flex-wrap">
            {['overview', 'revenue', 'customers', 'team', 'pipeline', 'tags', 'content'].map((view) => (
              <button
                key={view}
                onClick={() => setAnalyticsView(view)}
                className={`px-3 py-1.5 text-sm capitalize transition-colors ${
                  analyticsView === view 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-muted'
                }`}
                data-testid={`analytics-view-${view}`}
              >
                {view}
              </button>
            ))}
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchAnalytics}
            disabled={analyticsLoading}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${analyticsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {analyticsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !analyticsData && !enhancedAnalytics ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BarChart3 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Click refresh to load analytics data</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Overview View */}
          {analyticsView === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics Row - All Clickable */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card 
                  className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('Paid Invoices (This Month)', 'invoices', (inv) => {
                    const thisMonth = new Date().toISOString().slice(0, 7);
                    return inv.status === 'paid' && inv.paid_date?.startsWith(thisMonth);
                  })}
                  data-testid="metric-revenue"
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-emerald-700 font-medium">Paid Revenue (This Month) <MetricInfo metricKey="paid_revenue_month" /></p>
                        <p className="text-2xl font-bold text-emerald-900">
                          £{enhancedAnalytics?.revenue?.revenue_this_month?.toLocaleString() || 0}
                        </p>
                        <p className="text-xs text-emerald-600 flex items-center gap-1">
                          {enhancedAnalytics?.revenue?.invoices_this_month_count || 0} paid invoice(s)
                          <ChevronRight className="h-3 w-3" />
                        </p>
                        {enhancedAnalytics?.revenue?.pending_revenue > 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            £{enhancedAnalytics?.revenue?.pending_revenue?.toLocaleString()} pending
                          </p>
                        )}
                      </div>
                      <div className="p-3 bg-emerald-200/50 rounded-xl">
                        <PoundSterling className="h-6 w-6 text-emerald-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('All Customers', 'customers')}
                  data-testid="metric-customers"
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-700 font-medium">Total Customers <MetricInfo metricKey="customers_total" /></p>
                        <p className="text-2xl font-bold text-blue-900">{enhancedAnalytics?.customers?.total || allCustomers.length || 0}</p>
                        <p className="text-xs text-blue-600 flex items-center gap-1">
                          {enhancedAnalytics?.customers?.repeat_customers || 0} repeat ({enhancedAnalytics?.customers?.repeat_rate || 0}%)
                          <ChevronRight className="h-3 w-3" />
                        </p>
                      </div>
                      <div className="p-3 bg-blue-200/50 rounded-xl">
                        <Users className="h-6 w-6 text-blue-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('Scheduled Jobs', 'enquiries', (e) => e.status === 'scheduled' || e.status === 'booked')}
                  data-testid="metric-scheduled"
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-amber-700 font-medium">Calendar Jobs Booked <MetricInfo metricKey="calendar_jobs_booked" /></p>
                        <p className="text-2xl font-bold text-amber-900">{enhancedAnalytics?.summary?.total_jobs_scheduled ?? 0}</p>
                        <p className="text-xs text-amber-600 flex items-center gap-1">
                          {enhancedAnalytics?.forecast?.upcoming_jobs_count || 0} unique future bookings
                          <ChevronRight className="h-3 w-3" />
                        </p>
                      </div>
                      <div className="p-3 bg-amber-200/50 rounded-xl">
                        <Hammer className="h-6 w-6 text-amber-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('Accepted Quotes', 'quotes', (q) => q.status === 'accepted')}
                  data-testid="metric-conversion"
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-700 font-medium">Quote Conversion <MetricInfo metricKey="quote_conversion" /></p>
                        <p className="text-2xl font-bold text-purple-900">{enhancedAnalytics?.revenue?.quote_conversion_rate || 0}%</p>
                        <p className="text-xs text-purple-600 flex items-center gap-1">
                          {enhancedAnalytics?.summary?.quotes_accepted || 0}/{enhancedAnalytics?.summary?.total_quotes || allQuotes.length || 0} quote docs accepted
                          <ChevronRight className="h-3 w-3" />
                        </p>
                      </div>
                      <div className="p-3 bg-purple-200/50 rounded-xl">
                        <Target className="h-6 w-6 text-purple-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Website Traffic & Enquiries Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Eye className="h-4 w-4" /> Website Traffic
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Page Views (30d) <MetricInfo metricKey="page_views_30d" /></span>
                        <span className="font-semibold">{analyticsData?.page_views || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Unique Visitors (30d) <MetricInfo metricKey="unique_visitors_30d" /></span>
                        <span className="font-semibold">{analyticsData?.unique_visitors || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Conversion Rate <MetricInfo metricKey="site_conversion_rate" /></span>
                        <span className="font-semibold">{analyticsData?.conversion_rate || 0}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Enquiries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">This Month</span>
                        <span className="font-semibold">{analyticsData?.enquiries_this_month || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Last Month <MetricInfo metricKey="enquiries_last_month" /></span>
                        <span className="font-semibold">{analyticsData?.enquiries_last_month || 0}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Change</span>
                        <span className={`font-semibold ${(analyticsData?.enquiries_this_month || 0) >= (analyticsData?.enquiries_last_month || 0) ? 'text-green-600' : 'text-red-500'}`}>
                          {(analyticsData?.enquiries_this_month || 0) >= (analyticsData?.enquiries_last_month || 0) ? '+' : ''}
                          {(analyticsData?.enquiries_this_month || 0) - (analyticsData?.enquiries_last_month || 0)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Customers & Business Tips */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Crown className="h-4 w-4" /> Top Customers
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {enhancedAnalytics?.customers?.top_customers?.length > 0 ? (
                      <div className="space-y-2">
                        {enhancedAnalytics.customers.top_customers.slice(0, 5).map((customer, idx) => (
                          <div key={idx} className="flex justify-between items-center py-1 border-b last:border-0">
                            <span className="text-sm truncate max-w-[60%]">{customer.name}</span>
                            <span className="font-semibold text-sm">£{customer.revenue?.toLocaleString() || 0}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No customer data yet</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Business Tips
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {enhancedAnalytics?.tips?.map((tip, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <span className="text-primary">•</span>
                          <span className="text-muted-foreground">{tip}</span>
                        </div>
                      )) || (
                        <p className="text-muted-foreground text-center py-4">Tips will appear based on your data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Revenue View */}
          {analyticsView === 'revenue' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Invoiced <MetricInfo metricKey="total_invoiced" /></p>
                    <p className="text-2xl font-bold">£{enhancedAnalytics?.revenue?.total_invoiced?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Quoted <MetricInfo metricKey="total_quoted" /></p>
                    <p className="text-2xl font-bold">£{enhancedAnalytics?.revenue?.total_quoted?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-4">
                    <p className="text-sm text-emerald-700">Avg Job Value <MetricInfo metricKey="avg_job_value" /></p>
                    <p className="text-2xl font-bold text-emerald-800">£{averageJobValue?.toLocaleString() || enhancedAnalytics?.revenue?.avg_job_value?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Pending Quote Value <MetricInfo metricKey="pending_quote_value" /></p>
                    <p className="text-2xl font-bold text-amber-600">£{enhancedAnalytics?.revenue?.pending_quotes_value?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue by Service Type - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Revenue by Service Type</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueByService.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No revenue data available</p>
                  ) : (
                    <div className="space-y-3">
                      {revenueByService.map((service, idx) => {
                        const maxRevenue = Math.max(...revenueByService.map(s => s.revenue));
                        const percentage = maxRevenue > 0 ? (service.revenue / maxRevenue) * 100 : 0;
                        const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-pink-500', 'bg-cyan-500'];
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{service.service}</span>
                              <span>£{service.revenue.toLocaleString()} ({service.invoice_count} jobs)</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-3">
                              <div 
                                className={`${colors[idx % colors.length]} h-3 rounded-full transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quote Pipeline <MetricInfo metricKey="quotes_by_status" /></CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <div className="flex-1 text-center p-4 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{enhancedAnalytics?.summary?.quotes_pending || 0}</p>
                      <p className="text-sm text-muted-foreground">Pending</p>
                    </div>
                    <div className="flex-1 text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{enhancedAnalytics?.summary?.quotes_accepted || 0}</p>
                      <p className="text-sm text-muted-foreground">Accepted</p>
                    </div>
                    <div className="flex-1 text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{enhancedAnalytics?.summary?.quotes_declined ?? enhancedAnalytics?.summary?.quotes_rejected ?? 0}</p>
                      <p className="text-sm text-muted-foreground">Declined</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Customers View */}
          {analyticsView === 'customers' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Customers <MetricInfo metricKey="customers_total" /></p>
                    <p className="text-2xl font-bold">{enhancedAnalytics?.customers?.total || allCustomers.length || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Repeat Rate <MetricInfo metricKey="repeat_rate" /></p>
                    <p className="text-2xl font-bold">{enhancedAnalytics?.customers?.repeat_rate || 0}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-4">
                    <p className="text-sm text-emerald-700">Avg Lifetime Value <MetricInfo metricKey="avg_lifetime_value" /></p>
                    <p className="text-2xl font-bold text-emerald-800">£{customerLTV?.average_lifetime_value?.toLocaleString() || enhancedAnalytics?.customers?.avg_lifetime_value?.toLocaleString() || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">This Month <MetricInfo metricKey="customers_new_this_month" /></p>
                    <p className="text-2xl font-bold">{enhancedAnalytics?.customers?.new_this_month || 0}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Top Customers by Lifetime Value */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Top Customers by Lifetime Value</CardTitle>
                </CardHeader>
                <CardContent>
                  {customerLTV?.top_customers?.length > 0 ? (
                    <div className="space-y-2">
                      {customerLTV.top_customers.slice(0, 5).map((customer, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 border rounded-lg hover:bg-muted/50">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                              idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-muted'
                            }`}>
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-medium">{customer.customer_name}</p>
                              <p className="text-xs text-muted-foreground">{customer.paid_invoices} paid invoices • {customer.total_jobs} jobs</p>
                            </div>
                          </div>
                          <p className="font-bold text-emerald-600">£{customer.lifetime_value?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No customer value data available</p>
                  )}
                </CardContent>
              </Card>

              {enhancedAnalytics?.customers?.dormant_customers?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                      <AlertCircle className="h-4 w-4" /> Dormant Customers (6+ months)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {enhancedAnalytics.customers.dormant_customers.slice(0, 5).map((customer, idx) => (
                        <div key={idx} className="flex justify-between items-center py-1 border-b border-amber-200 last:border-0">
                          <span className="text-sm">{customer.name}</span>
                          <span className="text-xs text-muted-foreground">Last: {customer.last_job_date || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Top Customers by Revenue</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead className="text-right">Jobs</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enhancedAnalytics?.customers?.top_customers?.slice(0, 10).map((customer, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{customer.name}</TableCell>
                          <TableCell className="text-right">{customer.jobs || 0}</TableCell>
                          <TableCell className="text-right">£{customer.revenue?.toLocaleString() || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Team View */}
          {analyticsView === 'team' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {enhancedAnalytics?.team?.members?.map((member, idx) => (
                  <Card key={idx}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                          style={{ backgroundColor: member.color || '#6366F1' }}
                        >
                          {member.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-semibold">{member.name}</p>
                          <p className="text-sm text-muted-foreground">{member.email}</p>
                        </div>
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                        <div>
                          <p className="text-2xl font-bold">{member.jobs_count || 0}</p>
                          <p className="text-xs text-muted-foreground">Jobs</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{member.hours_worked || 0}</p>
                          <p className="text-xs text-muted-foreground">Hours</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )) || (
                  <Card className="md:col-span-2 lg:col-span-3">
                    <CardContent className="py-8 text-center">
                      <Users className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No team performance data available</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {enhancedAnalytics?.team?.members?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Team Summary <MetricInfo metricKey="team_summary" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Member</TableHead>
                          <TableHead className="text-right">Jobs</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Avg/Job</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {enhancedAnalytics.team.members.map((member, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{member.name}</TableCell>
                            <TableCell className="text-right">{member.jobs_count || 0}</TableCell>
                            <TableCell className="text-right">{member.hours_worked || 0}h</TableCell>
                            <TableCell className="text-right">
                              {member.jobs_count ? (member.hours_worked / member.jobs_count).toFixed(1) : 0}h
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Pipeline View - Enhanced with Full Conversion Funnel */}
          {analyticsView === 'pipeline' && (
            <div className="space-y-6">
              {/* Quick Stats Row - Clickable */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('All Enquiries', 'enquiries')}
                >
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Pipeline Entries <MetricInfo metricKey="pipeline_entries" /></p>
                    <p className="text-2xl font-bold">{pipelineStats?.total || 0}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">Click to view <ChevronRight className="h-3 w-3" /></p>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-amber-50 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('New Enquiries', 'enquiries', e => !e.status || e.status === 'new' || e.status === 'pending')}
                >
                  <CardContent className="pt-4">
                    <p className="text-sm text-amber-700">New <MetricInfo metricKey="pipeline_new" /></p>
                    <p className="text-2xl font-bold text-amber-800">{pipelineStats?.new || 0}</p>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-purple-50 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('Quoted Jobs', 'enquiries', e => e.status === 'quoted')}
                >
                  <CardContent className="pt-4">
                    <p className="text-sm text-purple-700">Quoted <MetricInfo metricKey="pipeline_quoted" /></p>
                    <p className="text-2xl font-bold text-purple-800">{pipelineStats?.quoted || 0}</p>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-blue-50 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('In Progress Jobs', 'enquiries', e => e.status === 'in-progress' || e.status === 'in_progress')}
                >
                  <CardContent className="pt-4">
                    <p className="text-sm text-blue-700">In Progress <MetricInfo metricKey="pipeline_in_progress" /></p>
                    <p className="text-2xl font-bold text-blue-800">{pipelineStats?.inProgress || 0}</p>
                  </CardContent>
                </Card>
                <Card 
                  className="bg-green-50 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openDrillDown('Paid Jobs', 'enquiries', e => e.status === 'paid' || e.status === 'closed')}
                >
                  <CardContent className="pt-4">
                    <p className="text-sm text-green-700">Paid <MetricInfo metricKey="pipeline_paid" /></p>
                    <p className="text-2xl font-bold text-green-800">{pipelineStats?.paid || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Full Conversion Funnel */}
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-primary" />
                        Business Journey Funnel <MetricInfo metricKey="funnel" />
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">Track where leads are in your pipeline - click any stage to see details</p>
                    </div>
                    {/* Iter 65+: one-click bridge — creates shadow enquiries
                        for historic invoices with no matching enquiry and
                        marks matched ones as converted. Idempotent. */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await axios.post(`${API}/admin/analytics/funnel-backfill`, {}, authHeaders);
                          const { toast } = await import('sonner');
                          toast.success(
                            `Bridged ${data.matched_to_existing_enquiry} matched + ${data.shadows_created} new shadow${data.shadows_created === 1 ? '' : 's'}`
                          );
                          fetchAnalyticsData();
                        } catch (err) {
                          const { toast } = await import('sonner');
                          toast.error('Funnel bridge failed');
                        }
                      }}
                      data-testid="funnel-bridge-btn"
                      title="Link every invoice back to an enquiry. Creates hidden shadow records for historical invoices with no original enquiry."
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Bridge Funnel
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {[
                      { key: 'total', label: 'Pipeline Entries', color: 'bg-slate-500', icon: MessageSquare, filter: null },
                      { key: 'siteVisit', label: 'Site Visits', color: 'bg-orange-500', icon: Eye, filter: e => e.status === 'site-visit' || e.status === 'inspection_scheduled' },
                      { key: 'quoted', label: 'At Quote Stage', color: 'bg-purple-500', icon: FileText, filter: e => e.status === 'quoted' },
                      { key: 'accepted', label: 'Accepted', color: 'bg-indigo-500', icon: CheckCircle, filter: e => e.status === 'accepted' || e.status === 'approved' },
                      { key: 'scheduled', label: 'Scheduled', color: 'bg-blue-500', icon: Calendar, filter: e => e.status === 'scheduled' || e.status === 'booked' },
                      { key: 'inProgress', label: 'In Progress', color: 'bg-cyan-500', icon: Clock, filter: e => e.status === 'in-progress' || e.status === 'in_progress' },
                      { key: 'completed', label: 'Completed', color: 'bg-teal-500', icon: Hammer, filter: e => e.status === 'completed' },
                      { key: 'invoiced', label: 'Invoiced', color: 'bg-emerald-500', icon: FileText, filter: e => e.status === 'invoiced' },
                      { key: 'paid', label: 'Paid', color: 'bg-green-600', icon: PoundSterling, filter: e => e.status === 'paid' || e.status === 'closed' },
                      { key: 'reviewReceived', label: 'Reviews Linked', color: 'bg-yellow-500', icon: Star, filter: e => e.feedback_id },
                    ].map((stage, idx) => {
                      const count = pipelineStats?.[stage.key] || 0;
                      const total = pipelineStats?.total || 1;
                      const percentage = stage.key === 'total' ? 100 : Math.round((count / total) * 100);
                      const Icon = stage.icon;
                      
                      return (
                        <div 
                          key={stage.key}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => openDrillDown(stage.label, 'enquiries', stage.filter)}
                          data-testid={`funnel-${stage.key}`}
                        >
                          <div className={`p-1.5 rounded ${stage.color} text-white flex-shrink-0`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-sm font-medium">{stage.label}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold">{count}</span>
                                <span className="text-xs text-muted-foreground">({percentage}%)</span>
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              </div>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className={`${stage.color} h-2 rounded-full transition-all duration-500`} 
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Drop-off Analysis */}
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      Where Leads Are Lost
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(() => {
                        const stages = [
                          { from: 'Enquiry', to: 'Quote', fromCount: pipelineStats?.total || 0, toCount: pipelineStats?.quoted || 0 },
                          { from: 'Quote', to: 'Accepted', fromCount: pipelineStats?.quoted || 0, toCount: pipelineStats?.accepted || 0 },
                          { from: 'Completed', to: 'Invoiced', fromCount: pipelineStats?.completed || 0, toCount: pipelineStats?.invoiced || 0 },
                          { from: 'Paid', to: 'Review', fromCount: pipelineStats?.paid || 0, toCount: pipelineStats?.reviewReceived || 0 },
                        ];
                        
                        return stages.map((s, idx) => {
                          const dropOff = s.fromCount > 0 ? Math.round(((s.fromCount - s.toCount) / s.fromCount) * 100) : 0;
                          const convRate = s.fromCount > 0 ? Math.round((s.toCount / s.fromCount) * 100) : 0;
                          
                          return (
                            <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="text-sm">{s.from} → {s.to}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-green-600 font-medium">{convRate}% convert</span>
                                {dropOff > 20 && (
                                  <Badge variant="destructive" className="text-xs">{dropOff}% lost</Badge>
                                )}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Quote to Job Rate</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-4">
                      <p className="text-4xl font-bold text-primary">{enhancedAnalytics?.revenue?.quote_conversion_rate || 0}%</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {enhancedAnalytics?.summary?.quotes_accepted || 0} of {enhancedAnalytics?.summary?.total_quotes || allQuotes.length} quotes accepted
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-4"
                        onClick={() => openDrillDown('All Quotes', 'quotes')}
                      >
                        View All Quotes <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Tags View */}
          {analyticsView === 'tags' && (
            <div className="space-y-6">
              {/* Tag Overview Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Jobs <MetricInfo metricKey="tags_total_jobs" /></p>
                    <p className="text-2xl font-bold">{tagAnalytics?.total_jobs || 0}</p>
                  </CardContent>
                </Card>
                <Card className="bg-indigo-50 border-indigo-200">
                  <CardContent className="pt-4">
                    <p className="text-sm text-indigo-700">Jobs with Tags <MetricInfo metricKey="jobs_with_tags" /></p>
                    <p className="text-2xl font-bold text-indigo-800">{tagAnalytics?.jobs_with_tags || 0}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Tag Adoption Rate <MetricInfo metricKey="tag_adoption_rate" /></p>
                    <p className="text-2xl font-bold">{tagAnalytics?.tag_adoption_rate || 0}%</p>
                  </CardContent>
                </Card>
              </div>

              {/* Tag Usage Breakdown - Horizontal Bar Chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" />
                    Tag Usage Breakdown
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">How tags are distributed across your jobs</p>
                </CardHeader>
                <CardContent>
                  {tagAnalytics?.tag_counts && Object.keys(tagAnalytics.tag_counts).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(tagAnalytics.tag_counts)
                        .sort(([, a], [, b]) => b - a)
                        .map(([tagId, count]) => {
                          const maxCount = Math.max(...Object.values(tagAnalytics.tag_counts));
                          const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          const tagInfo = JOB_TAGS[tagId];
                          const adoptionPct = tagAnalytics.total_jobs > 0 ? Math.round((count / tagAnalytics.total_jobs) * 100) : 0;
                          
                          return (
                            <div 
                              key={tagId} 
                              className="space-y-1 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
                              onClick={() => openDrillDown(
                                `Jobs tagged: ${tagInfo?.label || tagId}`, 
                                'enquiries', 
                                (e) => e.tags?.includes(tagId)
                              )}
                              data-testid={`tag-bar-${tagId}`}
                            >
                              <div className="flex justify-between text-sm items-center">
                                <div className="flex items-center gap-2">
                                  <Badge className={tagInfo?.bgColor || 'bg-gray-100 text-gray-800'}>
                                    {tagInfo?.label || tagId}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="font-bold">{count}</span>
                                  <span className="text-xs text-muted-foreground">({adoptionPct}% of jobs)</span>
                                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                </div>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2.5">
                                <div 
                                  className="h-2.5 rounded-full transition-all duration-500"
                                  style={{ 
                                    width: `${percentage}%`,
                                    backgroundColor: tagInfo?.color || '#6B7280'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No tags have been applied to jobs yet</p>
                      <p className="text-xs mt-1">Add tags to jobs in the Jobs tab to track patterns</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Tag Distribution by Stage */}
              {tagAnalytics?.tag_by_status && Object.keys(tagAnalytics.tag_by_status).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Tags by Job Stage</CardTitle>
                    <p className="text-sm text-muted-foreground">Which tags appear at each pipeline stage</p>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tag</TableHead>
                            <TableHead className="text-center">New</TableHead>
                            <TableHead className="text-center">Quoted</TableHead>
                            <TableHead className="text-center">Accepted</TableHead>
                            <TableHead className="text-center">In Progress</TableHead>
                            <TableHead className="text-center">Completed</TableHead>
                            <TableHead className="text-center">Paid</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(tagAnalytics.tag_by_status)
                            .sort(([a], [b]) => (tagAnalytics.tag_counts[b] || 0) - (tagAnalytics.tag_counts[a] || 0))
                            .map(([tagId, statuses]) => {
                              const tagInfo = JOB_TAGS[tagId];
                              return (
                                <TableRow key={tagId}>
                                  <TableCell>
                                    <Badge className={tagInfo?.bgColor || 'bg-gray-100'}>{tagInfo?.label || tagId}</Badge>
                                  </TableCell>
                                  {['new', 'quoted', 'accepted', 'in_progress', 'completed', 'paid'].map(status => (
                                    <TableCell key={status} className="text-center">
                                      {statuses[status] ? (
                                        <span className="font-medium">{statuses[status]}</span>
                                      ) : (
                                        <span className="text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Content View (Portfolio & Reviews) */}
          {analyticsView === 'content' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Portfolio Items <MetricInfo metricKey="portfolio_items" /></p>
                    <p className="text-2xl font-bold">{portfolioStats?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">{portfolioStats?.featured || 0} featured</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Total Reviews <MetricInfo metricKey="reviews_total" /></p>
                    <p className="text-2xl font-bold">{feedbackStats?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">{feedbackStats?.pending || 0} pending approval</p>
                  </CardContent>
                </Card>
                <Card className="bg-yellow-50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-yellow-700">Average Rating <MetricInfo metricKey="avg_rating" /></p>
                    <p className="text-2xl font-bold text-yellow-800 flex items-center gap-1">
                      {feedbackStats?.avgRating || 0} <Star className="h-5 w-5 fill-yellow-500 text-yellow-500" />
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">Featured Reviews <MetricInfo metricKey="featured_reviews" /></p>
                    <p className="text-2xl font-bold">{feedbackStats?.featured || 0}</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Portfolio by Category</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {portfolioStats?.byCategory && Object.keys(portfolioStats.byCategory).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(portfolioStats.byCategory).map(([category, count]) => (
                          <div key={category} className="flex justify-between items-center py-1 border-b last:border-0">
                            <span className="text-sm capitalize">{category.replace(/-/g, ' ')}</span>
                            <Badge variant="secondary">{count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No portfolio items yet</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Review Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 justify-center">
                      <div className="text-center p-4 bg-green-50 rounded-lg flex-1">
                        <p className="text-2xl font-bold text-green-600">{feedbackStats?.approved || 0}</p>
                        <p className="text-xs text-muted-foreground">Approved</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg flex-1">
                        <p className="text-2xl font-bold text-amber-600">{feedbackStats?.pending || 0}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Drill-Down Modal */}
      <Dialog open={drillDownOpen} onOpenChange={setDrillDownOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{drillDownTitle}</span>
              <Badge variant="secondary">{drillDownData.length} records</Badge>
            </DialogTitle>
            <DialogDescription>
              Click on any record to view details
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[60vh] pr-4">
            {drillDownData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No records found for this filter</p>
              </div>
            ) : (
              <div className="space-y-2">
                {drillDownData.map((item, idx) => (
                  <div key={item.id || idx}>
                    {renderDrillDownItem(item, drillDownType)}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
